import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Output } from "valibot";

import { ChatDataSchema } from "components/SonicBot/schemas";

function useHandleScroll(chatData: Output<typeof ChatDataSchema>) {
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);

  const scrollTopRef = useRef(0);
  const chatDataContainerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useDebouncedCallback(
    () => {
      const { current: chatDataContainer } = chatDataContainerRef;
      if (!chatDataContainer) {
        return;
      }
      chatDataContainer.scrollTo({
        top: chatDataContainer.scrollHeight,
        behavior: "smooth",
      });
    },
    300,
    {
      leading: true,
      trailing: true,
      maxWait: 300,
    },
  );

  useLayoutEffect(() => {
    const { current: chatDataContainer } = chatDataContainerRef;
    if (!chatDataContainer) {
      return;
    }

    scrollTopRef.current = 0;

    // Assuming 100ms will be enough for react-markdown to complete render
    chatDataContainer.style.opacity = "0%";
    setTimeout(() => {
      chatDataContainer.scrollTop = chatDataContainer.scrollHeight;
      chatDataContainer.style.opacity = "100%";
      setAutoScrollEnabled(true);
    }, 100);
  }, [chatDataContainerRef]);

  useEffect(() => {
    const { current: innerContainer } = innerContainerRef;

    if (!innerContainer || !autoScrollEnabled) {
      return;
    }

    const resizeObserver = new ResizeObserver(scrollToBottom);

    resizeObserver.observe(innerContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollToBottom, autoScrollEnabled]);

  useEffect(() => {
    if (!chatData.length && !autoScrollEnabled) {
      setAutoScrollEnabled(true);
    }
  }, [chatData.length, autoScrollEnabled]);

  const handleScroll = () => {
    const { current: chatDataContainer } = chatDataContainerRef;
    const { current: scrollTop } = scrollTopRef;
    if (!chatDataContainer || typeof scrollTop !== "number") {
      return;
    }

    const scrolledUp = chatDataContainer.scrollTop < scrollTop;
    const scrolledBelowThreshold =
      chatDataContainer.scrollHeight -
        chatDataContainer.scrollTop -
        chatDataContainer.clientHeight <
      5;

    if (autoScrollEnabled && scrolledUp && !scrolledBelowThreshold) {
      // Need to disable because user scrolled up
      setAutoScrollEnabled(false);
    } else if (!autoScrollEnabled && scrolledBelowThreshold) {
      // Need to enable because scrolled below threshold
      setAutoScrollEnabled(true);
    }

    scrollTopRef.current = chatDataContainer.scrollTop;
  };

  return {
    chatDataContainerRef,
    innerContainerRef,
    autoScrollEnabled,
    handleScroll,
    scrollToBottom,
  };
}

export default useHandleScroll;
